import { Avatar, Box, Button, Chip, CircularProgress, Dialog, Divider, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useState } from "react";
import { usePagination } from "../../content-strategy/hooks/usePagination";
import { ReviewAutomationActivityData, ReviewAutomationActivityItem } from "../types";
import { usePostReviewAutomationResponse } from "../hooks";
import { posthogService, PostHogEvent } from "src/common/services/posthogService";

function formatDate(dateStr?: string | number): string {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return String(dateStr);
  }
}

interface AIActivityProps {
  activityData?: ReviewAutomationActivityData;
}



function AIActivity({ activityData }: AIActivityProps) {

  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState("All");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [postingId, setPostingId] = useState<string | number | null>(null);
  const [postedIds, setPostedIds] = useState<(string | number)[]>([]);
  const [activityToConfirm, setActivityToConfirm] = useState<ActivityItem | null>(null);

  const { postResponse } = usePostReviewAutomationResponse();

  const handlePost = (activity: ActivityItem) => {
    const responseId = activity.rawId || activity.id;
    if (!responseId) return;

    setPostingId(activity.id);
    postResponse(
      {
        responseId: String(responseId),
        payload: {
          action: "post",
          draft: activity.reply || ""
        }
      },
      {
        onSuccess: () => {
          setPostedIds((prev) => [...prev, activity.id]);
          setPostingId(null);
          setActivityToConfirm(null);
        },
        onError: () => {
          setPostingId(null);
          setActivityToConfirm(null);
        }
      }
    );
  };

  const handleCopy = (text: string, id: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);

    // Track Orbit AI review/reply text copied in PostHog
    posthogService.track(PostHogEvent.ORBIT_AI_REVIEW_REPLY_COPIED, {
      type: id.startsWith('review') ? 'review' : 'reply'
    });
  };

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (value) => {
    setSelected(value);
    handleClose();

    // Track Orbit AI Review Automation activity filter tab changed in PostHog
    posthogService.track(PostHogEvent.ORBIT_AI_REVIEW_FILTER_CHANGED, {
      filter_status: value
    });
  };

  interface ActivityItem {
    id: string | number;
    rawId?: string;
    name: string;
    avatar: string;
    photo?: string;
    date: string;
    rating: number;
    status: string;
    review: string;
    reply: string;
    isPosted: boolean;
    postedBy?: string;
  }

  const mappedItems: ActivityItem[] = (activityData?.items && activityData.items.length > 0)
    ? activityData.items.map((item: ReviewAutomationActivityItem, index: number): ActivityItem => ({
      id: item._id || index,
      rawId: item._id,
      name: item.review_snapshot?.reviewer_name || "Anonymous",
      avatar: (item.review_snapshot?.reviewer_name?.trim()?.[0] || "A").toUpperCase(),
      photo: item.review_snapshot?.reviewer_photo || "",
      date: formatDate(item.review_snapshot?.create_time || item.created_at),
      rating: item.review_snapshot?.star_rating ?? 5,
      status: (item.status === "external" || item.status === "sent" || item.status === "Sent") ? "Posted" : (item.status?.replace("_", " ") || "Pending Approval"),
      review: item.review_snapshot?.comment || "",
      reply: item.google_reply?.comment || item.final_reply || item.draft_reply || "",
      isPosted: (item.posted_at ?? 0) > 0 || item.status === "Sent" || item.status === "external" || item.status?.toLowerCase() === "posted",
      postedBy: item.posted_by || "",
    }))
    : [];

  const filteredActivities = mappedItems.filter((activity: ActivityItem) => {
    const s = activity.status.toLowerCase();
    if (selected === "All") return true;
    if (selected === "Pending") return s === "pending approval" || s === "pending";
    if (selected === "Approved") return s === "approved" || s === "sent" || s === "posted";
    if (selected === "Rejected") return s === "rejected";
    return true;
  });

  const {
    currentItems,
    page,
    totalPages,
    handlePrev,
    handleNext,
    isFirstPage,
    isLastPage,
  } = usePagination(filteredActivities, 3);

  const chipSx = (status = "") => {
    const s = status.toLowerCase();
    const isGreen = s === "sent" || s === "posted" || s === "approved";
    const isRed = s === "pending approval" || s === "pending" || s === "rejected" || s === "cancelled" || s === "canceled";

    return {
      height: 20,
      borderRadius: 2,
      bgcolor: isGreen
        ? "#ECFDF3"
        : isRed
          ? "#FEF3F2"
          : "#F2F4F7",

      "& .MuiChip-label": {
        px: 1.5,
      },
    };
  };

  const hasItems = mappedItems.length > 0;

  return (
    <div>
      <div className="flex justify-between items-center p-6">
        <div className="flex items-center gap-2">
          <p className="text-base font-medium text-[#475467]">
            AI Activity
          </p>
          <Button
            sx={{
              height: "40px",
              fontFamily: "Inter",
              fontSize: "var(--font-size-14)",
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: 0,
              backgroundColor: "var(--color-white)",
              color: "var(--color-text-primary)",
              border: "1px solid var(--color-border-muted)",
              "&:hover": {
                border: "1px solid var(--color-border-muted)",
                backgroundColor: "var(--color-white)",
              },
            }}
            variant="outlined"
            disabled={!hasItems}
            onClick={handleOpen}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  transition: "0.2s",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            }
          >
            Approval Status: {selected}
          </Button>

          <Menu
            disableScrollLock
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem
              selected={selected === "All"}
              onClick={() => handleSelect("All")}
            >
              All
            </MenuItem>

            <MenuItem
              selected={selected === "Approved"}
              onClick={() => handleSelect("Approved")}
            >
              Approved
            </MenuItem>

            <MenuItem
              selected={selected === "Pending"}
              onClick={() => handleSelect("Pending")}
            >
              Pending
            </MenuItem>

            <MenuItem
              selected={selected === "Rejected"}
              onClick={() => handleSelect("Rejected")}
            >
              Rejected
            </MenuItem>
          </Menu>
        </div>

        <div className="flex items-center gap-2">
          <p className="text-primary text-base">
            {page}/{totalPages}
          </p>

          <div className="flex items-center">
            <IconButton
              size="small"
              aria-label="previous"
              onClick={handlePrev}
              disabled={isFirstPage || !hasItems}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>

            <IconButton
              size="small"
              aria-label="next"
              onClick={handleNext}
              disabled={isLastPage || !hasItems}
            >
              <NavigateNextIcon />
            </IconButton>
          </div>
        </div>
      </div>

      <Divider />


      {currentItems.map((activity) => (
        <React.Fragment key={activity.id}>
          <div className="px-6 py-4">
            <Box>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                  }}
                >
                  <Avatar
                    src={(activity as any).photo || undefined}
                    sx={{
                      bgcolor: "#C54BEF",
                      width: 30,
                      height: 30,
                    }}
                  >
                    {activity.avatar}
                  </Avatar>

                  <Box>
                    <Typography
                      fontWeight={500}
                      fontSize={16}
                      color="#475467"
                    >
                      {activity.name}
                    </Typography>

                    <Stack
                      direction="row"
                      mt={0.5}
                      flexWrap="wrap"
                      gap= "6px"
                    >
                      <Chip
                        sx={{
                          ...chipSx(),
                          bgcolor: "#F5F5F5",
                        }}
                        label={
                          <Typography fontSize={12} fontWeight={500} color="#414651">
                            Date: {activity.date}
                          </Typography>
                        }
                      />
                      <Chip
                        sx={{
                          ...chipSx(),
                          bgcolor: "#F5F5F5",
                        }}
                        label={
                          <Typography fontSize={12} fontWeight={500} color="#414651">
                            Rating: {"★".repeat(activity.rating)}
                          </Typography>
                        }
                      />
                      {(() => {
                        const isExternal = activity.postedBy === "external";
                        const isLocallyPosted = postedIds.includes(activity.id);
                        const rawStatus = isLocallyPosted
                          ? "Posted"
                          : activity.status;
                        const displayStatus = (rawStatus === "Sent" || rawStatus === "sent") ? "Posted" : rawStatus;
                        const s = displayStatus.toLowerCase();
                        const isPostedState =
                          s === "sent" || s === "posted" || s === "approved";

                        const labelText =
                          isPostedState && !isExternal
                            ? "Posted by Orbit AI"
                            : `Status: ${displayStatus}`;

                        return (
                          <Chip
                            sx={chipSx(displayStatus)}
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography
                                  fontSize={12}
                                  fontWeight={500}
                                  color={isPostedState ? "#027A48" : "#B42318"}
                                  sx={{
                                    textTransform:
                                      isPostedState && !isExternal
                                        ? "none"
                                        : "capitalize"
                                  }}
                                >
                                  {labelText}
                                </Typography>
                                {isPostedState && !isExternal && (
                                  <img
                                    src="/static/images/icons/ai_visibility.svg"
                                    alt="ai_visibility"
                                    width={16}
                                    style={{ display: "inline-block" }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        );
                      })()}
                    </Stack>
                  </Box>
                </Box>

                {!activity.isPosted && !postedIds.includes(activity.id) && (
                  (() => {
                    const isCancelled =
                      activity.status?.toLowerCase() === "cancelled" ||
                      activity.status?.toLowerCase() === "canceled";

                    return (
                      <Button
                        variant="contained"
                        disabled={isCancelled || postingId === activity.id}
                        onClick={isCancelled ? undefined : () => setActivityToConfirm(activity)}
                        sx={{
                          padding: "16px 10px",
                          fontSize: 14,
                          maxHeight: 40
                        }}
                      >
                        Post
                      </Button>
                    );
                  })()
                )}
              </Box>

              {/* Review */}
              <Box
                sx={{
                  display: "flex",
                  mt: 1,
                  gap: 1,
                  padding: 1,
                }}
              >
                <Typography
                  fontWeight={600}
                  flex={1}
                  fontSize={12}
                  color="#2B2F36"
                >
                  {activity?.review || "--"}
                </Typography>
                {activity?.review &&
                  <Tooltip title={copiedId === `review-${activity.id}` ? "Copied!" : "Copy Review"} placement="top" arrow>
                    <IconButton
                      sx={{
                        padding: 0
                      }}
                      size="small"
                      onClick={() => handleCopy(activity.review, `review-${activity.id}`)}
                    >
                      <img
                        src="/static/images/icons/copyIcon.svg"
                        width={18}
                        alt="Copy Review"
                      />
                    </IconButton>
                  </Tooltip>
                }
              </Box>

              {/* Reply*/}
              {activity.reply && (
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    mt={1}
                    paddingLeft={2}
                    borderLeft={1}
                    borderColor="#A8A6BF"
                  >
                    <img
                      src="/static/images/icons/ai_visibility.svg"
                      alt="copy"
                      width={18}
                    />

                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#2B2F36",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Reply
                    </Typography>

                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: "#EFF0F6",
                        borderRadius: "8px",
                        p: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        flex={1}
                        fontSize={12}
                        color="#2B2F36"
                      >
                        {activity.reply}
                      </Typography>

                      <Tooltip title={copiedId === `reply-${activity.id}` ? "Copied!" : "Copy Reply"} placement="top" arrow>
                        <IconButton
                          sx={{
                            padding: 0
                          }}
                          size="small"
                          onClick={() => handleCopy(activity.reply, `reply-${activity.id}`)}
                        >
                          <img
                            src="/static/images/icons/copyIcon.svg"
                            width={18}
                            alt="Copy Reply"
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Box>
          </div>

          <Divider />
        </React.Fragment>
      ))}
      {currentItems.length === 0 && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography
            sx={{
              color: '#667085',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            No reviews found for this profile
          </Typography>
        </Box>
      )}

      <Dialog
        open={activityToConfirm !== null}
        onClose={() => {
          if (postingId === null) {
            setActivityToConfirm(null);
          }
        }}
        disableScrollLock
      >
        <div className="p-6 min-w-[400px] max-w-[500px] relative">
          <p className="text-primary font-semibold text-xl mb-4">
            Confirm Changes
          </p>
          <p className="text-sm text-[#475467] mb-6">
            Are you sure you want to post this reply? Please note that this reply will be posted directly to your Google Business Profile.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outlined"
              className="w-24 px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
              disabled={postingId !== null}
              onClick={() => setActivityToConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="h-10 px-6 w-24 min-w-24"
              disabled={postingId !== null}
              onClick={async () => {
                if (activityToConfirm) {
                  await handlePost(activityToConfirm);
                }
              }}
            >
              {postingId !== null ? <CircularProgress size={16} color="inherit" /> : 'Confirm'}
            </Button>
          </div>
        </div>
      </Dialog>

    </div >
  )
}

export default AIActivity